import { invertRecord } from "shared/helpers/record";
import type {
  FacebookSocialIntegration,
  InstagramSocialIntegration,
  LinkedinSocialIntegration,
  OrganisationSocialIntegration,
  TiktokSocialIntegration,
  TwitterSocialIntegration,
} from "shared/resources";
import { type ValueOf } from "shared/types";

export const FACEBOOK_SDK_CONFIG = {
  appId: import.meta.env.VITE_FACEBOOK_APP_ID,
  cookie: true,
  localStorage: true,
  version: "v17.0",
  xfbml: false,
};

export const FACEBOOK_SDK_URL = "https://connect.facebook.net/en_US/sdk.js";

export enum SocialPlatform {
  BLOG = "Blog",
  BLUESKY = "Bluesky",
  FACEBOOK = "Facebook",
  FORUM = "Forum",
  INSTAGRAM = "Instagram",
  LINKEDIN = "LinkedIn",
  NEWSLETTER = "Newsletter",
  PINTEREST = "Pinterest",
  PODCAST = "Podcast",
  REDDIT = "Reddit",
  SUBSTACK = "Substack",
  TIKTOK = "Tiktok",
  TWITTER = "Twitter",
  YOUTUBE = "YouTube",
}

export const SOCIAL_PLATFORM_POSTABLE = [
  "Facebook",
  "Instagram",
  "LinkedIn",
  "Twitter",
] as const;

export type SocialPlatformPostable = (typeof SOCIAL_PLATFORM_POSTABLE)[number];

export type SocialPlatformWithCharacterLimit =
  | SocialPlatformPostable
  | "Tiktok";

interface SocialPlatformIntegrationMap {
  FacebookSocialIntegration: FacebookSocialIntegration;
  InstagramSocialIntegration: InstagramSocialIntegration;
  LinkedinSocialIntegration: LinkedinSocialIntegration;
  TiktokSocialIntegration: TiktokSocialIntegration;
  TwitterSocialIntegration: TwitterSocialIntegration;
}

export type SocialPlatformIntegration = ValueOf<SocialPlatformIntegrationMap>;
export type SocialPlatformIntegrationName = keyof SocialPlatformIntegrationMap;

interface SocialPlatformPostableIntegrationMap {
  FacebookSocialIntegration: FacebookSocialIntegration;
  InstagramSocialIntegration: InstagramSocialIntegration;
  LinkedinSocialIntegration: LinkedinSocialIntegration;
  TwitterSocialIntegration: TwitterSocialIntegration;
}

export type SocialPlatformPostableIntegration =
  ValueOf<SocialPlatformPostableIntegrationMap>;

export type SocialPlatformPostableIntegrationName =
  keyof SocialPlatformPostableIntegrationMap;

export type SocialIntegration = InstanceType<
  typeof OrganisationSocialIntegration
>;

export type TokenTimestamps = Pick<
  SocialIntegration,
  "expiresAt" | "expiredAt" | "tokenRefreshedAt"
>;

export const SOCIAL_ENGAGE_PLATFORM_TOKEN_EXPIRY: Record<
  SocialPlatformWithCharacterLimit,
  number
> = {
  [SocialPlatform.FACEBOOK]: 5184000000, // 60 * DAY_IN_MILLISECONDS
  [SocialPlatform.INSTAGRAM]: 5184000000, // 60 * DAY_IN_MILLISECONDS
  [SocialPlatform.LINKEDIN]: 5184000000, // 60 * DAY_IN_MILLISECONDS
  [SocialPlatform.TIKTOK]: 31536000000, // 365 * DAY_IN_MILLISECONDS
  [SocialPlatform.TWITTER]: Infinity, // Never expires
};

export const WIDGET_LIST = [
  { label: "Social vs Earned Media", value: "stream_vs_stream" },
  { label: "Mentions By Platform", value: "coverage_volume_breakdown" },
  { label: "Mentions Timeline", value: "coverage_volume" },
  { label: "Share Of Voice Timeline", value: "competitor_benchmark" },
  { label: "Share Of Voice", value: "share_of_voice" },
  { label: "Sentiment Breakdown", value: "sentiment_breakdown" },
  { label: "Sentiment Timeline", value: "sentiment_timeline" },
  { label: "Themes", value: "word_cloud" },
  { label: "Leading Hashtags", value: "leading_hashtags" },
  { label: "Emoji Count", value: "emoji_count" },
  { label: "Top Tweets", value: "top_tweets" },
  { label: "Top Facebook Posts", value: "top_facebook_posts" },
  { label: "Top Facebook Pages", value: "top_facebook_pages" },
  { label: "Twitter Influencers", value: "key_twitter_influencers" },
  { label: "Instagram Influencers", value: "key_instagram_influencers" },
  { label: "Top Bluesky Posts", value: "top_bluesky_posts" },
  { label: "Top Instagram Posts", value: "top_instagram_posts" },
  { label: "Top LinkedIn Posts", value: "top_linkedin_posts" },
  { label: "Top TikTok Videos", value: "top_tiktok_videos" },
  { label: "Top YouTube Videos", value: "top_youtube_videos" },
  { label: "Bluesky Influencers", value: "key_bluesky_influencers" },
  { label: "YouTube Influencers", value: "key_youtube_influencers" },
];

export const TWO_OR_MORE_STREAM_WIDGETS = [
  "competitor_benchmark",
  "share_of_voice",
];

export const socialGraphsPalette = [
  "#FFAE6C",
  "#FF7875",
  "#F759AB",
  "#B37FEB",
  "#597EF7",
  "#69C0FF",
  "#13C2C2",
  "#7CED95",
  "#FFE58F",
  "#FFCCC7",
  "#FFADD2",
  "#D3ADF7",
  "#ADC6FF",
  "#BAE7FF",
  "#87E8DE",
  "#C3F7CF",
];

export const ENGAGE_POST_TYPES = {
  carousel: {
    icon: "https://assets.streem.com.au/icons/engage-carousel.svg",
    textKeypath: "social.engage_post_types.carousel",
  },
  image: {
    icon: "https://assets.streem.com.au/icons/engage-image.svg",
    textKeypath: "social.engage_post_types.image",
  },
  job: {
    icon: "https://assets.streem.com.au/icons/engage-job.svg",
    textKeypath: "social.engage_post_types.job",
  },
  link_preview: {
    icon: "https://assets.streem.com.au/icons/engage-link-preview.svg",
    textKeypath: "social.engage_post_types.link_preview",
  },
  multiple_attachments: {
    icon: "https://assets.streem.com.au/icons/engage-multiple-attachments.svg",
    textKeypath: "social.engage_post_types.multiple_attachments",
  },
  quote_tweet: {
    icon: "https://assets.streem.com.au/icons/engage-quote-tweet.svg",
    textKeypath: "social.engage_post_types.quote_tweet",
  },
  reel: {
    icon: "https://assets.streem.com.au/icons/engage-reel.svg",
    textKeypath: "social.engage_post_types.reel",
  },
  reply_tweet: {
    icon: "https://assets.streem.com.au/icons/engage-reply-tweet.svg",
    textKeypath: "social.engage_post_types.reply_tweet",
  },
  shared: {
    icon: "https://assets.streem.com.au/icons/engage-shared.svg",
    textKeypath: "social.engage_post_types.shared",
  },
  streem: {
    icon: "https://assets.streem.com.au/icons/engage-streem.svg",
    textKeypath: "social.engage_post_types.streem",
  },
  text: {
    icon: "https://assets.streem.com.au/icons/engage-text.svg",
    textKeypath: "social.engage_post_types.text",
  },
  video: {
    icon: "https://assets.streem.com.au/icons/engage-video.svg",
    textKeypath: "social.engage_post_types.video",
  },
};

export const PLATFORM_LABELS = {
  FacebookSocialIntegration: SocialPlatform.FACEBOOK,
  InstagramSocialIntegration: SocialPlatform.INSTAGRAM,
  LinkedinSocialIntegration: SocialPlatform.LINKEDIN,
  TiktokSocialIntegration: SocialPlatform.TIKTOK,
  TwitterSocialIntegration: SocialPlatform.TWITTER,
} as const satisfies Record<SocialPlatformIntegrationName, SocialPlatform>;

export const PLATFORM_LABELS_LOOKUP = invertRecord(PLATFORM_LABELS);

export const SOCIAL_PLATFORM_POSTABLE_INTEGRATIONS: SocialPlatformPostableIntegrationName[] =
  SOCIAL_PLATFORM_POSTABLE.map((platform) => PLATFORM_LABELS_LOOKUP[platform]);

export const FB_PERMISSIONS = [
  "public_profile",
  "email",
  "pages_show_list",
  "pages_read_engagement",
  "pages_manage_posts",
  "read_insights",
  "instagram_basic",
  "instagram_content_publish",
  "instagram_manage_insights",
  "instagram_manage_comments",
  "business_management",
] as const;

export const CSL_PACKAGES = [
  {
    id: "CSL5Q15S",
    label: "Up to 5 searches & 1,500 sample rate per day",
  },
  {
    id: "CSL10Q15S",
    label: "Up to 10 searches & 1,500 sample rate per day",
  },
  {
    id: "CSL15Q15S",
    label: "Up to 15 searches & 1,500 sample rate per day",
  },
  {
    id: "CSL20Q15S",
    label: "Up to 20 searches & 1,500 sample rate per day",
  },
  {
    id: "CSL5Q50S",
    label: "Up to 5 searches & 5,000 sample rate per day",
  },
  {
    id: "CSL10Q50S",
    label: "Up to 10 searches & 5,000 sample rate per day",
  },
  {
    id: "CSL15Q50S",
    label: "Up to 15 searches & 5,000 sample rate per day",
  },
  {
    id: "CSL20Q50S",
    label: "Up to 20 searches & 5,000 sample rate per day",
  },
  {
    id: "CSL30Q50S",
    label: "Up to 30 searches & 5,000 sample rate per day",
  },
];

export const SOCIAL_ICONS = {
  [SocialPlatform.BLOG]: "img:https://assets.streem.global/icons/blog.svg",
  [SocialPlatform.BLUESKY]:
    "img:https://assets.streem.global/icons/bluesky.svg",
  [SocialPlatform.FACEBOOK]:
    "img:https://assets.streem.global/icons/facebook.svg",
  [SocialPlatform.FORUM]: "img:https://assets.streem.global/icons/forum.svg",
  [SocialPlatform.INSTAGRAM]:
    "img:https://assets.streem.global/icons/instagram.svg",
  [SocialPlatform.LINKEDIN]:
    "img:https://assets.streem.global/icons/linkedin.svg",
  [SocialPlatform.NEWSLETTER]:
    "img:https://assets.streem.global/icons/newsletter.svg",
  [SocialPlatform.PINTEREST]:
    "img:https://assets.streem.global/icons/pinterest.svg",
  [SocialPlatform.PODCAST]:
    "img:https://assets.streem.global/icons/podcast.svg",
  [SocialPlatform.REDDIT]:
    "img:https://assets.streem.global/icons/exorde-reddit.svg",
  [SocialPlatform.SUBSTACK]:
    "img:https://assets.streem.global/icons/substack.svg",
  [SocialPlatform.TIKTOK]: "img:https://assets.streem.global/icons/tiktok.svg",
  [SocialPlatform.TWITTER]:
    "img:https://assets.streem.global/icons/twitter.svg",
  [SocialPlatform.YOUTUBE]:
    "img:https://assets.streem.global/icons/youtube.svg",
} as const satisfies Record<SocialPlatform, string>;

export const SOCIAL_BRAND_COLORS = {
  [SocialPlatform.BLOG]: "#F06A35",
  [SocialPlatform.BLUESKY]: "#0085FF",
  [SocialPlatform.FACEBOOK]: "#395185",
  [SocialPlatform.FORUM]: "#FFB649",
  [SocialPlatform.INSTAGRAM]: "#B331BA",
  [SocialPlatform.LINKEDIN]: "#0077B5",
  [SocialPlatform.NEWSLETTER]: "#FF6B35",
  [SocialPlatform.PINTEREST]: "#E60023",
  [SocialPlatform.PODCAST]: "#9000F0",
  [SocialPlatform.REDDIT]: "#FF4500",
  [SocialPlatform.SUBSTACK]: "#FF6719",
  [SocialPlatform.TIKTOK]: "#FE2C55",
  [SocialPlatform.TWITTER]: "#0F1419",
  [SocialPlatform.YOUTUBE]: "#E92722",
} as const satisfies Record<SocialPlatform, string>;

export const SOCIAL_ENGAGE_POST_STATES = {
  draft: "0",
  error: "5",
  live: "3",
  partially_sent: "8",
  pending_approval: "1",
  queued: "4",
  rejected: "7",
  scheduled: "2",
} as const;

export const SOCIAL_ENGAGE_POST_STATE_LOOKUP = invertRecord(
  SOCIAL_ENGAGE_POST_STATES
);

export type SocialEngagePostEventType = ValueOf<
  typeof SOCIAL_ENGAGE_POST_EVENT_LOOKUP
>;

export const SOCIAL_ENGAGE_POST_EVENT_LOOKUP = {
  1: "created",
  2: "edited",
  3: "approved",
  4: "post published",
  5: "scheduled",
  6: "approval requested",
  7: "unpublish requested",
  8: "post removed",
  9: "delete requested",
  10: "deleted",
  11: "unpublish approved",
  12: "returned to draft",
  13: "rejected",
  14: "post failed",
  15: "retried",
} as const;

export const SOCIAL_ENGAGE_CHARACTER_LIMITS: Record<
  Lowercase<
    keyof Pick<
      typeof SocialPlatform,
      "FACEBOOK" | "TWITTER" | "INSTAGRAM" | "LINKEDIN" | "TIKTOK"
    >
  >,
  number
> = {
  facebook: 63206,
  instagram: 2200,
  linkedin: 3000,
  tiktok: 2200,
  twitter: 280,
};

export const SOCIAL_ALT_TEXT_CHARACTER_LIMITS = {
  Facebook: Number.MAX_SAFE_INTEGER, // No defined limit
  Instagram: 1000,
  LinkedIn: 4086,
  Twitter: 1000,
} as const satisfies Record<SocialPlatformPostable, number>;

export function isSocialPlatformPostable(
  platform: string
): platform is SocialPlatformPostable {
  return (SOCIAL_PLATFORM_POSTABLE as readonly string[]).includes(platform);
}

export const INSTAGRAM_MONITOR_MAX_HASHTAG_COUNT = 30;

export const INSTAGRAM_MONITOR_COOLING_PERIOD_DAYS = 7;

export const INSTAGRAM_MONITOR_COOLING_PERIOD_MS =
  INSTAGRAM_MONITOR_COOLING_PERIOD_DAYS * 86400000;

export const SOCIAL_PREFIX = {
  handle: "@",
  hashtag: "#",
} as const;
